import { App } from './App';
import { LoginPage } from './LoginPage';
import { ForgotPasswordPage } from './ForgotPasswordPage';
import { ResetPasswordPage } from './ResetPasswordPage';
import { ProfilePage } from './ProfilePage';
import { AbandonedVehicleListPage } from './AbandonedVehicleListPage';
import { ErrorPage } from './ErrorPage';
import { AuthRoute } from './components/ProtectedRoute';
import { DatasetListPage } from './DatasetListPage';
import { DatasetImageListPage } from './DatasetImageListPage';
import { ImageListPage } from "./ImageListPage";
import { ReportsPage } from "./ReportsPage";
import { ReportObbDet } from "./reports/ReportObbDet";
import { imageListLoader } from "./loaders/imageListLoader";
import { abandonedVehicleLoader, abandonedVehicleOnlyLoader } from "./loaders/abandonedVehicleLoader";
import { DetectionListPage } from "./DetectionListPage";
import { detectionLoader } from "./loaders/detectionLoader";
import { datasetImageListLoader } from "./loaders/datasetImageListLoader";
import { DatasetPaintPage } from "./DatasetPaintPage";
import { DetectionRunListPage } from "./DetectionRunListPage";
import { runListLoader } from "./loaders/runListLoader";
import { datasetImageLoader } from "./loaders/datasetImageLoader";
import { DatasetImageObbAnnotationPage } from "./DatasetImageAnnotationPage";
import { HexagonVisualizationPage } from "./AbandonedVehicleHex";
import { AbandonedVehicleChicago311 } from "./AbandonedVehiclesChicago311";
import { chicagoLoader } from "./loaders/chicagoLoader";
import { detectionLoaderFromFile } from "./loaders/detectionLoaderFromFile";
import { OrbitDemo2 } from "./OrbitDemo";
import { OrbitFeasibilityStudyPage } from "./OrbitFeasibilityStudyPage";
import { ScoutPage } from "./ScoutPage";
import { DashboardPage } from "./DashboardPage";
import { AbandonedVehicleRunListPage } from "./AbandonedVehicleRunListPage";
import { abandonedVehicleRunListLoader } from "./loaders/abandonedVehicleRunListLoader";
import { RouteObject, ShouldRevalidateFunctionArgs } from "react-router-dom";
import { datasetListLoader } from "./loaders/datasetListLoader";
import { tleLoader } from "./loaders/tleLoader";
import { PairReviewPage } from "./PairReviewPage";
import { CadModelAnnotationPage } from "./CadModelAnnotationPage";
import { cadModelListLoader } from "./loaders/cadModelListLoader";
import { ImageAlignPage } from "./ImageAlignmentPage";
import { imageAlignLoader } from "./loaders/imageAlignLoader";
import { ThemePage } from "./ThemePage";
import { AbandonedVehicleReviewPage } from "./AbandonedVehicleReviewPage";
import { AbandonedVehicleSituationPage } from "./AbandonedVehicleReviewSituationPage";
import { AbandonedVehicleMapPage } from "./AbandonedVehicleMapPage";

function auth( x: React.ReactElement ) {
    return <AuthRoute>{x}</AuthRoute>;
}

function reloadOnRunIdChange( { currentParams, nextParams }: ShouldRevalidateFunctionArgs ) {
  return currentParams.runId !== nextParams.runId;
}

function datasetImageRevalidate( { currentParams, nextParams }: ShouldRevalidateFunctionArgs ) {
    return currentParams.datasetImageId !== nextParams.datasetImageId;
}

function aerialImageRevalidate( { currentParams, nextParams }: ShouldRevalidateFunctionArgs ) {
    return currentParams.aerialImageId !== nextParams.aerialImageId;
}

export const routes: RouteObject[] = [{
    path: "/", element: <App />, errorElement: <ErrorPage />, children: [
        { path: "login",  element: <LoginPage          /> },
        { path: "forgot", element: <ForgotPasswordPage /> },
        { path: "reset",  element: <ResetPasswordPage  /> },

        //sales and new customer pipeline
        { path: "/scout", element: auth( <ScoutPage /> ), loader: undefined },

        //protected
        { path: "/", element: auth( <DashboardPage /> ) }, //show overview
        { path: "/image/:aerialImageId?",
            element: auth( <ImageListPage /> ),
            loader: imageListLoader,
            shouldRevalidate: aerialImageRevalidate
        }, 
        {   //show captured images            
            path: "/abandoned/run",
            element: auth( <AbandonedVehicleRunListPage /> ), 
            loader: abandonedVehicleRunListLoader
        },        
        //abandoned vehicle list page
        { path: "/detect/:runId",
            id: "run",
            element: auth( <AbandonedVehicleListPage /> ), 
            loader: abandonedVehicleLoader, 
            shouldRevalidate: reloadOnRunIdChange,
            children: [{
                id:      "run-vehicle",
                path:    ":vehicleId",
                element: <></>,
                loader:  abandonedVehicleOnlyLoader, // just the one vehicle
            }],
        },
        //annotation map page
        { path: "/detect/:runId/map",
            id: "run-map",
            element: auth( <AbandonedVehicleMapPage /> ), 
            loader: abandonedVehicleLoader, 
            shouldRevalidate: reloadOnRunIdChange,
            children: [{
                id:      "run-map-vehicle",
                path:    ":vehicleId",
                element: <></>,
                loader:  abandonedVehicleOnlyLoader, // just the one vehicle
            }],
        },
        { path: "/detect/:runId/review",               element: auth( <AbandonedVehicleReviewPage    /> ), loader: abandonedVehicleLoader },
        { path: "/detect/:runId/situation",            element: auth( <AbandonedVehicleSituationPage /> ), loader: abandonedVehicleLoader },
        {   //show both images and all matches
            path:    "/detect/:runId/align", 
            element: auth( <ImageAlignPage /> ), 
            loader:  imageAlignLoader,
        },
        //old raw detection review pages
        { path: "/detect/",                                 element: auth( <DetectionRunListPage /> ), loader: runListLoader },                
        { path: "/detect/:aerialImageId/obj/:runId",        element: auth( <DetectionListPage    /> ), loader: detectionLoader },        
        { path: "/detect/:aerialImageId/obj/:runId/",       element: auth( <DetectionListPage    /> ), loader: detectionLoader },
        { path: "/detect/static/:aerialImageId/obj/:file/", element: auth( <DetectionListPage    /> ), loader: detectionLoaderFromFile },
        {
            path: "/report",
            element: auth( <ReportsPage /> ),
            children: [
                { index: true, element: <ReportObbDet />, loader: detectionLoader },
                { path: "detect/:aerialImageId/:runId", element: <ReportObbDet />, loader: detectionLoader },
            ]
        },
        { path: "/profile", element: auth( <ProfilePage /> ), },

        //dataset management
        {   path: "/dataset", element: auth( <DatasetListPage /> ), loader: datasetListLoader },
        {   path: "/dataset/:datasetId",
            element: auth( <DatasetImageListPage /> ), 
            loader:  datasetImageListLoader,
            shouldRevalidate: ({ currentUrl, nextUrl }) => currentUrl.pathname !== nextUrl.pathname, 
        },         
        {   path: "/dataset/:datasetId/stats", element: auth( <DatasetImageListPage /> ), loader: datasetImageListLoader },                
        {   path: "/dataset/:datasetId/image/:datasetImageId/:annotationId?", 
            element: auth( <DatasetImageObbAnnotationPage /> ), 
            loader: datasetImageLoader,
            shouldRevalidate: datasetImageRevalidate
        },
        //experiments
        {   path: "/dataset/paint", element: auth( <DatasetPaintPage /> )                                                                                                          },

        //benchmarks
        {   path: "/dataset/bench/:benchmarkId"                                                                                                          },
        {   path: "/hex/:aerialImageAId/:aerialImageBId", element: <HexagonVisualizationPage />,           loader: abandonedVehicleLoader },        

        //globe and satellite tracking     
        {   path: "/globe",  element: auth( <OrbitDemo2  /> ),               loader: tleLoader },
        {   path: "/orbit",  element: auth( <OrbitFeasibilityStudyPage /> ), loader: undefined },                

        //experimental
        // { path:    "/detect/:aerialImageAId/:aerialImageBId/:runAId/:runBId", 
        //   element: auth( <AbandonedVehicleListPageInBrowserCompare /> ), 
        //   loader:  abandonedVehicleLoaderInBrowserCompare },
        { path: "/research/chicago",                      element: <AbandonedVehicleChicago311 />, loader: chicagoLoader },
        { path: "pairs/:aerialImageAId/:aerialImageBId/", element: auth( <PairReviewPage /> ), loader: abandonedVehicleLoader },
        { path: "cad", element: auth( <CadModelAnnotationPage /> ), loader: cadModelListLoader },
        { path: "theme", element: auth( <ThemePage /> ) }
    ]
}];